import React, { useEffect, useMemo, useRef } from 'react';
import Header from '../../components/common/global-top-bar';
import Spinner from '../../components/common/spinner';

import { useForm } from 'react-hook-form';
import { CustomInput } from '../../components/common';
import { numberonBlur, numberOnChange } from '../../components/common/useForm';
import { SettingsResponse, useSettings, useUpdateSettings } from '../../hooks/useSettings';

export const blackListType = {
  'Phone Number': 'mobile_phone',
  'Email Address': 'email_address',
  'IP Address': 'ip_address',
};

type state = {
  showModal: boolean;
  resourceId: number;
  name: string;
};

const defaultState = {
  showModal: false,
  resourceId: 0,
  name: '',
};
const LOCVARIABLES: Partial<Record<keyof SettingsResponse['system'], 'number' | 'text' | 'boolean' | 'decimal'>> = {
  LOC_CALLER_CALL_START_DEADMAN_TIMER: 'number',
  LOC_CALLER_MOOD_COLLECTION_REMINDER_TIMER: 'number',
  LOC_CALLER_VOICEMAIL_DETECTED_TIMEOUT: 'number',
  LOC_CALL_CENTS_PER_MINUTE: 'number',
  LOC_CALL_LENGTH_SETTLE_THRESHOLD: 'number',
  LOC_DEADMAN_TIMER: 'number',
  LOC_DEFAULT_CALL_DURATION: 'number',
  LOC_EXPERIENCE_PAYOUT_THRESHOLD: 'number',
  LOC_FORCE_WEB_APP: 'boolean',
  LOC_LISTENER_BACKGROUND_CHECK_FEE: 'number',
  LOC_LISTENER_BACKGROUND_CHECK_REWARD: 'number',
  LOC_LISTENER_CALL_START_DEADMAN_TIMER: 'number',
  LOC_LISTENER_CALL_START_IVR_COUNT: 'number',
  LOC_LISTENER_CALL_START_IVR_TIMEOUT: 'number',
  LOC_LISTENER_DISCOVERY_MAX_DISPLAY: 'number',
  LOC_LISTENER_INITIAL_CALLOUT_DELAY: 'number',
  LOC_LISTENER_REWARD_CALL_REQUIREMENT: 'number',
  LOC_LISTENER_ROLE_MAXIMUM_COUNT: 'number',
  LOC_LISTENER_VOICEMAIL_DETECTED_DEADMAN_TIMER: 'number',
  LOC_MINIMUM_LISTENER_EXPERIENCES: 'number',
  LOC_MINIMUM_PEER_EXPERIENCES: 'number',
  LOC_UNLIMITED_MINUTES_RESERVE_AMOUNT: 'number',
  LOC_WEB_APP_URL: 'text',
  TRUSTED_SESSION_TTL: 'number',
  UNTRUSTED_SESSION_TTL: 'number',
  SEARCH_CONTENT_POOL_SIZE: 'number',
  SEARCH_PEOPLE_POOL_SIZE: 'number',
  SEARCH_RESOURCES_POOL_SIZE: 'number',
  SEARCH_SCORE_THRESHOLD: 'decimal',
  CHAT_DEADMAN_TIMER_MINUTES: 'number',
  CRISIS_COOLDOWN_MINUTES: 'number',
};

type IndividualSetting = {
  value: string | number | boolean;
  name: string;
  onSubmit: (data: any) => void;
  inputType?: 'text' | 'number' | 'boolean' | 'decimal';
};

const IndividualSetting: React.FunctionComponent<IndividualSetting> = ({
  value,
  name,
  onSubmit,
  inputType = 'text',
}): JSX.Element => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: useMemo(() => {
      if (value) {
        return { [name]: value };
      }
    }, [value, name]),
  });

  const cancelRef = useRef() as React.MutableRefObject<HTMLButtonElement>;

  useEffect(() => {
    value && reset({ [name]: value as string | number | true });
  }, [value, reset, name]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mx-3 my-5 flex justify-between">
        <p>{name}</p>
        <div className="flex">
          <CustomInput
            inputType={inputType}
            Label={''}
            register={{
              ...register(name, {
                onChange: (e) => {
                  if (inputType === 'number') {
                    numberOnChange(e, name, setValue);
                  }
                  cancelRef.current.classList.remove('bg-gray-background-dark');
                  cancelRef.current.classList.add('bg-red');
                },
                onBlur: (e) => {
                  if (inputType === 'number') {
                    numberonBlur(e, name, setValue);
                  }
                },
                valueAsNumber: inputType === 'number',
              }),
            }}
            //@ts-ignore
            Error={!!errors[name]?.message?.length > 0}
            //@ts-ignore
            ErrorMessage={errors[name]?.message}
            dataTestId={`settings-${name}-input`}
          />

          <div className="flex justify-end">
            {' '}
            <button
              className="bg-bright-blue mx-2 h-6 w-20 rounded-full px-2 text-center text-sm text-white"
              type="submit"
              onClick={() => {
                cancelRef.current.classList.remove('bg-red');
                cancelRef.current.classList.add('bg-gray-background-dark');
              }}
              disabled={false}
              data-testid="settings-save-button"
            >
              Save
            </button>
            <button
              type="button"
              ref={cancelRef}
              className="bg-gray-background-dark mx-2 h-6 w-20 rounded-full px-2 text-center text-sm text-white"
              onClick={() => {
                reset({ [name]: value as string | number | true });
                cancelRef.current.classList.remove('bg-red');
                cancelRef.current.classList.add('bg-gray-background-dark');
              }}
              data-testid="settings-cancel-button"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

const Settings: React.FunctionComponent = (): JSX.Element => {
  const { data: appSettings, isLoading } = useSettings('email');
  const updateSettings = useUpdateSettings();

  const onSubmit = (data: any) => {
    const payload = {
      data: {
        message: appSettings?.message,
        system: { ...appSettings?.system, ...data },
      },
    };

    updateSettings.mutate(payload);
  };

  return (
    <div>
      <Header heading={'Settings'} />
      <div className="max-window-height overflow-y-auto pb-10">
        <div className="w-full px-7">
          {isLoading && <Spinner />}
          <div className="text-gray-dark sticky mb-2 grid h-12 w-full grid-cols-12 items-center border-b-2 border-black text-left font-bold">
            <div className="col-span-5 font-bold">
              <p className="pl-10 capitalize">Name</p>
            </div>
            <div className="col-span-7">Type </div>
          </div>
          <div className="w-full overflow-y-scroll">
            {appSettings?.system ? (
              Object.keys(LOCVARIABLES)?.map((variable) => (
                <IndividualSetting
                  key={variable}
                  name={variable}
                  value={appSettings?.system[variable as keyof typeof appSettings.system]}
                  onSubmit={onSubmit}
                  inputType={LOCVARIABLES[variable as keyof typeof LOCVARIABLES]}
                />
              ))
            ) : (
              <p className="blue-primary ml-3 py-2 text-xl font-normal capitalize">No Found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
